import { Button } from "@components/ui/Button"
import { personaListValue } from "@lib/persona"
import { Pencil, Users } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"

interface Props {
  persona: Record<string, unknown> | null
  /** Opens the student's edit at Personalización (PRD-40 §FR-19). */
  onEdit: () => void
}

/**
 * Personalization, rendered inside the identity card rather than as its own
 * collapsed section at the bottom of the page.
 *
 * The move is a claim about what this data IS. Interests, goals and preferred
 * context are not analytics the professor drills into — they are part of
 * knowing who the student is, which is exactly what the header card answers.
 * Sitting last and collapsed, it read as an appendix and stayed shut; sitting
 * under the name, it does its job at a glance, which is the moment the
 * professor is deciding what note to write or what material to generate.
 *
 * Always expanded, so no state and no toggle — and its "Editar" is never
 * behind one. There is no `teacher_notes` here — those moved to the note
 * timeline and this row is shared with every other professor at the school
 * (PRD-40 §FR-7). Editing it opens the student's edit at this section.
 */
export const ProfilePersonaBlock = memo(function ProfilePersonaBlock({ persona, onEdit }: Props) {
  const { t } = useTranslation("student-profile")
  const raw = persona && typeof persona === "object" ? persona : {}

  // Only groups that actually carry values are rendered. A student who
  // answered before PRD-46 often has interests and nothing else; three
  // columns with two of them blank reads as a rendering bug.
  const groups = (["interests", "goals", "preferred_context"] as const)
    .map((key) => ({ key, values: personaListValue((raw as Record<string, unknown>)[key]) }))
    .filter((group) => group.values.length > 0)

  return (
    <div className="border-hairline mt-4 border-t pt-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h2 className="text-ink-subtle text-xs font-semibold">{t("persona.title")}</h2>
          {/* The counterpart of the notes' «Solo tú ves esto»: this row is
              every professor's, and it shapes the student's materials. */}
          <span className="text-ink-subtle inline-flex items-center gap-1 text-xs">
            <Users className="h-3.5 w-3.5" aria-hidden="true" />
            {t("persona.shared")}
          </span>
        </div>
        {groups.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onEdit}
            aria-label={t("persona.edit_aria")}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            {t("persona.edit")}
          </Button>
        )}
      </div>

      {groups.length === 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-ink-subtle text-sm">{t("persona.empty")}</p>
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            {t("persona.add")}
          </Button>
        </div>
      ) : (
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((group) => (
            <div key={group.key}>
              <dt className="text-ink-subtle text-xs font-medium tracking-wide uppercase">
                {t(`persona.${group.key}`)}
              </dt>
              <dd className="mt-1 flex flex-wrap gap-1">
                {group.values.map((value) => (
                  <span
                    key={value}
                    className="bg-inset text-ink-muted rounded-full px-2 py-0.5 text-xs"
                  >
                    {value}
                  </span>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
})
